<script lang="ts">
  import { login } from '../lib/auth';
  import { ApiError } from '../lib/api';

  interface Props {
    defaultEmail?: string;
    onAuthed: () => void;
    onWantSignup: () => void;
    onWantRecovery: () => void;
    /** When false, hide the "Opprett konto" CTA — self-registry is closed
     *  and the user needs an invite link instead. */
    signupAvailable?: boolean;
  }
  let { defaultEmail = '', onAuthed, onWantSignup, onWantRecovery,
        signupAvailable = true }: Props = $props();

  // `defaultEmail` is intentionally used only for the initial value. The warning
  // about referencing a non-state value in `$state(...)` is the right shape of
  // warning, but the wrong target here — we don't want reactive bidirectional
  // tracking of the prop.
  // svelte-ignore state_referenced_locally
  let email = $state(defaultEmail);
  let password = $state('');
  let error: string | null = $state(null);
  let busy = $state(false);

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    error = null;
    busy = true;
    try {
      await login(email.trim().toLowerCase(), password);
      password = '';
      onAuthed();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        error = 'Feil epost eller passord.';
      } else if (err instanceof ApiError && err.status === 404) {
        error = 'Ingen bruker med den eposten.';
      } else if (err instanceof Error && err.message.includes('decrypt')) {
        // libsodium throws a generic decryption error — that means the password
        // derived a KEK that doesn't unwrap. We landed here despite the server
        // accepting the verifier, which shouldn't happen unless the DB is
        // tampered with. Treat as wrong password.
        error = 'Klarte ikke å låse opp dataene dine.';
      } else {
        error = 'Innlogging feilet.';
      }
    } finally {
      busy = false;
    }
  }
</script>

<form onsubmit={submit} class="card" aria-labelledby="login-h">
  <h2 id="login-h">Logg inn</h2>

  <label for="login-email">Epost</label>
  <input id="login-email" type="email" autocomplete="username"
         bind:value={email} required />

  <label for="login-password">Passord</label>
  <input id="login-password" type="password" autocomplete="current-password"
         bind:value={password} required />

  {#if error}<p class="error" role="alert">{error}</p>{/if}

  <div class="row" style="margin-top: 1rem;">
    <button class="primary" type="submit" disabled={busy}>
      {busy ? 'Logger inn …' : 'Logg inn'}
    </button>
    {#if signupAvailable}
      <button type="button" onclick={onWantSignup}>Opprett konto</button>
    {/if}
    <button type="button" onclick={onWantRecovery}>Bruk gjenopprettingskode</button>
  </div>
  {#if !signupAvailable}
    <p class="muted" style="margin-top: 0.5rem;">
      Selvregistrering er stengt. Be om en invitasjonslenke fra noen som
      allerede har konto.
    </p>
  {/if}
</form>
